/**
 * Rating System Constants
 * Centralized constants for the rating and review system
 */

// App color scheme constants (based on your pizza theme)
export const APP_COLORS = {
    // Primary colors
    CHEESE: '#FED7AA', // Orange-200 - warm cheese color
    PEPPERONI: '#DC2626', // Red-600 - pepperoni red
    CRUST: '#92400E', // Yellow-800 - crust brown

    // Background colors
    WHITE: '#FFFFFF',
    GRAY_50: '#F9FAFB',
    GRAY_100: '#F3F4F6',
    GRAY_200: '#E5E7EB',

    // Status colors
    SUCCESS: '#10B981', // Green-500
    WARNING: '#F59E0B', // Amber-500
    ERROR: '#EF4444', // Red-500
    INFO: '#3B82F6' // Blue-500
};

// Rating criteria configuration
export const RATING_CRITERIA_CONFIG = {
    overall: {
        key: 'overall',
        label: 'Overall Experience',
        icon: '⭐',
        description: 'Your overall satisfaction with this restaurant',
        required: true,
        weight: 1.0
    },
    foodQuality: {
        key: 'foodQuality',
        label: 'Food Quality',
        icon: '🍕',
        description: 'Taste, freshness, and quality of the food',
        required: false,
        weight: 0.9
    },
    deliverySpeed: {
        key: 'deliverySpeed',
        label: 'Delivery Speed',
        icon: '🚚',
        description: 'How quickly your order was delivered',
        required: false,
        weight: 0.8
    },
    customerService: {
        key: 'customerService',
        label: 'Customer Service',
        icon: '👥',
        description: 'Quality of customer support and communication',
        required: false,
        weight: 0.7
    },
    valueForMoney: {
        key: 'valueForMoney',
        label: 'Value for Money',
        icon: '💰',
        description: 'Whether the price was worth the quality received',
        required: false,
        weight: 0.8
    },
    packaging: {
        key: 'packaging',
        label: 'Packaging',
        icon: '📦',
        description: 'Quality and presentation of food packaging',
        required: false,
        weight: 0.6
    }
};

// UI Configuration
export const UI_CONFIG = {
    // Pagination
    DEFAULT_PAGE_SIZE: 10,
    MAX_PAGE_SIZE: 50,

    // Star rating sizes
    STAR_SIZES: {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6',
        xl: 'w-8 h-8'
    },

    // Animation durations
    ANIMATION_DURATION: {
        fast: 150,
        normal: 300,
        slow: 500
    },

    // Breakpoints for responsive design
    BREAKPOINTS: {
        sm: '640px',
        md: '768px',
        lg: '1024px',
        xl: '1280px'
    }
};

// Form validation configuration
export const FORM_CONFIG = {
    // Character limits
    REVIEW_MAX_LENGTH: 500,
    REVIEW_MIN_LENGTH: 10,
    ADMIN_NOTES_MAX_LENGTH: 1000,

    // Validation rules
    RATING_MIN: 1,
    RATING_MAX: 5,

    // Debounce delays
    SEARCH_DEBOUNCE: 300,
    AUTO_SAVE_DEBOUNCE: 1000,

    // Form timeouts
    SUBMIT_TIMEOUT: 30000,
    AUTO_SAVE_TIMEOUT: 60000
};

// API Configuration
export const API_CONFIG = {
    // Endpoints
    BASE_PATH: '/ratings',
    ENDPOINTS: {
        CREATE: '',
        GET_RESTAURANT: '/restaurant/:id',
        GET_RESTAURANT_STATS: '/restaurant/:id/stats',
        GET_USER_RATING: '/user/restaurant/:id',
        TOP_RESTAURANTS: '/top-restaurants',
        MARK_HELPFUL: '/:id/helpful',
        REPORT: '/:id/report',
        DELETE: '/:id',
        REPORTED: '/reported',
        MODERATE: '/:id/moderate'
    },

    // Request timeouts
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,

    // Cache durations (in milliseconds)
    CACHE_DURATION: {
        RATINGS: 5 * 60 * 1000, // 5 minutes
        ANALYTICS: 10 * 60 * 1000, // 10 minutes
        USER_RATING: 2 * 60 * 1000 // 2 minutes
    }
};

// Business rules
export const BUSINESS_RULES = {
    // Rating constraints
    EDIT_WINDOW_DAYS: 7,
    MIN_RATINGS_FOR_ANALYTICS: 5,
    MAX_REPORTS_PER_USER_PER_DAY: 10,

    // Display rules
    SHOW_VERIFIED_BADGE: true,
    SHOW_ANONYMOUS_RATINGS: true,
    REQUIRE_LOGIN_FOR_RATING: true,

    // Moderation rules
    AUTO_HIDE_THRESHOLD: 5, // Number of reports to auto-hide
    ADMIN_REVIEW_REQUIRED: true
};

// Sort options for ratings
export const SORT_OPTIONS = {
    NEWEST: {
        key: 'newest',
        label: 'Newest First',
        sortBy: 'createdAt',
        sortOrder: 'desc'
    },
    OLDEST: {
        key: 'oldest',
        label: 'Oldest First',
        sortBy: 'createdAt',
        sortOrder: 'asc'
    },
    HIGHEST_RATED: {
        key: 'highest',
        label: 'Highest Rated',
        sortBy: 'rating',
        sortOrder: 'desc'
    },
    LOWEST_RATED: {
        key: 'lowest',
        label: 'Lowest Rated',
        sortBy: 'rating',
        sortOrder: 'asc'
    },
    MOST_HELPFUL: {
        key: 'helpful',
        label: 'Most Helpful',
        sortBy: 'helpful',
        sortOrder: 'desc'
    }
};

// Filter options
export const FILTER_OPTIONS = {
    RATING_LEVELS: [
        { value: 5, label: '5 Stars', color: 'text-green-600' },
        { value: 4, label: '4+ Stars', color: 'text-green-500' },
        { value: 3, label: '3+ Stars', color: 'text-yellow-500' },
        { value: 2, label: '2+ Stars', color: 'text-orange-500' },
        { value: 1, label: '1+ Stars', color: 'text-red-500' }
    ],

    TIME_PERIODS: [
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: '90d', label: 'Last 3 months' },
        { value: '1y', label: 'Last year' },
        { value: 'all', label: 'All time' }
    ]
};

// Chart configuration for analytics
export const CHART_CONFIG = {
    COLORS: {
        PRIMARY: APP_COLORS.CHEESE,
        SECONDARY: APP_COLORS.PEPPERONI,
        SUCCESS: APP_COLORS.SUCCESS,
        WARNING: APP_COLORS.WARNING,
        ERROR: APP_COLORS.ERROR,
        GRID: APP_COLORS.GRAY_200,
        TEXT: APP_COLORS.CRUST
    },

    DEFAULTS: {
        HEIGHT: 300,
        RESPONSIVE: true,
        MAINTAIN_ASPECT_RATIO: false,
        ANIMATION_DURATION: 750
    },

    TREND_CHART: {
        POINT_RADIUS: 4,
        LINE_WIDTH: 2,
        FILL: false
    },

    BAR_CHART: {
        BAR_THICKNESS: 20,
        MAX_BAR_THICKNESS: 40
    }
};

// Notification messages
export const NOTIFICATION_MESSAGES = {
    SUCCESS: {
        RATING_SUBMITTED: 'Thank you! Your rating has been submitted successfully.',
        RATING_UPDATED: 'Your rating has been updated successfully.',
        RATING_DELETED: 'Your rating has been deleted.',
        HELPFUL_MARKED: 'Thank you for marking this review as helpful!',
        REPORT_SUBMITTED: 'Thank you for reporting this review. We will investigate.',
        MODERATION_COMPLETED: 'Moderation action completed successfully.'
    },

    ERROR: {
        RATING_FAILED: 'Failed to submit your rating. Please try again.',
        LOAD_FAILED: 'Failed to load ratings. Please refresh the page.',
        NETWORK_ERROR: 'Network error. Please check your connection.',
        UNAUTHORIZED: 'Please log in to rate restaurants.',
        ALREADY_RATED: 'You have already rated this restaurant.',
        EDIT_EXPIRED: 'Rating can only be edited within 7 days.'
    },

    WARNING: {
        UNSAVED_CHANGES: 'You have unsaved changes. Are you sure you want to leave?',
        DELETE_CONFIRMATION: 'Are you sure you want to delete this rating?',
        REPORT_CONFIRMATION: 'Are you sure you want to report this review?'
    }
};

// Accessibility configuration
export const A11Y_CONFIG = {
    // ARIA labels
    ARIA_LABELS: {
        STAR_RATING: 'Star rating',
        RATE_RESTAURANT: 'Rate this restaurant',
        HELPFUL_BUTTON: 'Mark as helpful',
        REPORT_BUTTON: 'Report this review',
        EDIT_RATING: 'Edit your rating',
        DELETE_RATING: 'Delete your rating',
        FILTER_RATINGS: 'Filter ratings',
        SORT_RATINGS: 'Sort ratings'
    },

    // Screen reader text
    SR_TEXT: {
        STAR_FILLED: 'filled star',
        STAR_EMPTY: 'empty star',
        VERIFIED_PURCHASE: 'verified purchase',
        ANONYMOUS_REVIEW: 'anonymous review',
        HELPFUL_VOTES: 'helpful votes',
        RATING_OUT_OF_FIVE: 'out of 5 stars'
    },

    // Keyboard navigation
    KEYBOARD: {
        ENTER: 'Enter',
        SPACE: ' ',
        ARROW_LEFT: 'ArrowLeft',
        ARROW_RIGHT: 'ArrowRight',
        ARROW_UP: 'ArrowUp',
        ARROW_DOWN: 'ArrowDown',
        ESCAPE: 'Escape',
        TAB: 'Tab'
    }
};

// Performance configuration
export const PERFORMANCE_CONFIG = {
    // Lazy loading
    LAZY_LOAD_THRESHOLD: 100, // pixels
    LAZY_LOAD_ROOT_MARGIN: '50px',

    // Virtual scrolling
    VIRTUAL_SCROLL_ITEM_HEIGHT: 120,
    VIRTUAL_SCROLL_BUFFER: 5,

    // Debouncing
    SEARCH_DEBOUNCE: 300,
    RESIZE_DEBOUNCE: 100,
    SCROLL_DEBOUNCE: 16, // ~60fps

    // Caching
    MAX_CACHE_SIZE: 100,
    CACHE_TTL: 5 * 60 * 1000 // 5 minutes
};

// Development/Debug configuration
export const DEBUG_CONFIG = {
    ENABLE_LOGGING: process.env.NODE_ENV === 'development',
    LOG_LEVELS: {
        ERROR: 0,
        WARN: 1,
        INFO: 2,
        DEBUG: 3
    },
    LOG_COLORS: {
        ERROR: '#EF4444',
        WARN: '#F59E0B',
        INFO: '#3B82F6',
        DEBUG: '#6B7280'
    }
};

// Export all constants as default
const RATING_CONSTANTS = {
    APP_COLORS,
    RATING_CRITERIA_CONFIG,
    UI_CONFIG,
    FORM_CONFIG,
    API_CONFIG,
    BUSINESS_RULES,
    SORT_OPTIONS,
    FILTER_OPTIONS,
    CHART_CONFIG,
    NOTIFICATION_MESSAGES,
    A11Y_CONFIG,
    PERFORMANCE_CONFIG,
    DEBUG_CONFIG
};

export default RATING_CONSTANTS;